/**
 * FieldComposable.js is the Composition API equivalent of the AbstractField.vue
 * file in the Options API.
 */

import { computed, ref, toRefs } from 'vue';
import { pf } from '@/utils/vue-formatter';

export const fieldProps = {
    index:
    {
        type:     Number,
        required: true,
    },

    side:
    {
        type:     String,
        required: true,
    },

    parameter:
    {
        type:    Object,
        default: () => ({}),
    },

    name:
    {
        type:     String,
        required: true,
    },

    error:
    {
        type:    String,
        default: null,
    },

    modelValue:
    {
        type:    [String, Number, Object, Boolean, Array],
        default: null,
    },
};

export function useFieldComposable(props, emit, store)
{
    // ------------------------------------------------------------ PROPS

    const {
        index,
        side,
        parameter,
        name,
        error,
        modelValue,
    } = toRefs(props);


    // ------------------------------------------------------------ DATA

    const isTouched = ref(false);
    const isFocused = ref(false);


    // ------------------------------------------------------------ COMPUTED

    const tabIndex = computed(() =>
    {
        return side.value === 'left' ? 2 : 1;
    });

    const tempValue = computed({
        get()
        {
            if(isFocused.value)
            {
                return props.modelValue;
            }

            // @todo Must return formatted values.
            return props.modelValue;
        },

        set(value)
        {
            if(value === null)
            {
                return;
            }

            this.$emit('update:modelValue', value);
        },
    });

    const supplement = computed(() =>
    {
        if(parameter.value.priced_article_code)
        {
            const article = store.getters['catalog/getByCode'](parameter.value.priced_article_code);
            if(article)
            {
                return `+ ${this.max2decimals(article.price)} ${article.currency_code}`;
            }
        }

        return null;
    });

    const formattedValue = computed(() =>
    {
        return format(modelValue.value);
    });


    // ------------------------------------------------------------ METHODS

    function max2decimals(price)
    {
        return parseFloat(price).toFixed(2).replace(/\.0{0,2}$/, '');
    };

    function format(value)
    {
        if(value === null)
        {
            return null;
        }

        return pf(value, parameter.value.code, 'value');
    };

    function onFocus()
    {
        isFocused.value = true;
    }

    function onBlur()
    {
        isFocused.value = false;
        isTouched.value = true;
    }


    // ------------------------------------------------------------ EXPORT

    return {
        // Props (as data)
        index,
        side,
        parameter,
        name,
        error,
        modelValue,

        // Data
        isTouched,
        isFocused,

        // Computed
        tabIndex,
        tempValue,
        supplement,
        formattedValue,

        // Methods
        max2decimals,
        format,
        onFocus,
        onBlur,
    };
};
